import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';

export const CalendarSyncModal = ({ isOpen, onClose, onSyncComplete }) => {
  const [googleConnected, setGoogleConnected] = useState(false);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const googleToken = localStorage.getItem('google_calendar_token');
    const microsoftToken = localStorage.getItem('microsoft_calendar_token');
    setGoogleConnected(!!googleToken);
    setMicrosoftConnected(!!microsoftToken);
  }, []);

  const connectGoogleCalendar = async () => {
    const clientId = '53637039038-d06mhtr6jaq2hu2fprq7525nsf6hq411.apps.googleusercontent.com';
    const redirectUri = window.location.origin;
    const scope = 'https://www.googleapis.com/auth/calendar.readonly';
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${redirectUri}&` +
      `response_type=token&` +
      `scope=${scope}&` +
      `prompt=consent`;
    
    window.location.href = authUrl;
  };

  const connectMicrosoftCalendar = async () => {
    const clientId = 'a7211e12-4eb2-46fe-8a8a-32fcb32039e3';
    const redirectUri = window.location.origin;
    const scope = 'Calendars.Read offline_access';
    
    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=token&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}&` +
      `prompt=consent`;
    
    window.location.href = authUrl;
  };

  const syncGoogleEvents = async () => {
    const token = localStorage.getItem('google_calendar_token');
    if (!token) return;

    setSyncing(true);
    try {
      const timeMin = new Date().toISOString();
      const timeMax = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
      
      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events?` +
        `timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch events');
      
      const data = await response.json();
      const events = data.items.map(event => ({
        text: event.summary,
        time: event.start.dateTime ? new Date(event.start.dateTime).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }) : 'All day',
        description: event.description || '',
        completed: false,
        date: new Date(event.start.dateTime || event.start.date),
        source: 'google',
        externalId: event.id,
        location: event.location,
        isExternal: true
      }));
      
      localStorage.setItem('last_calendar_sync', new Date().toISOString());
      
      if (onSyncComplete) {
        onSyncComplete(events, 'google');
      }
    } catch (error) {
      console.error('Google sync error:', error);
      if (error.message.includes('401')) {
        localStorage.removeItem('google_calendar_token');
        setGoogleConnected(false);
      }
    } finally {
      setSyncing(false);
    }
  };

  const syncMicrosoftEvents = async () => {
    const token = localStorage.getItem('microsoft_calendar_token');
    if (!token) return;

    setSyncing(true);
    try {
      const startDateTime = new Date().toISOString();
      const endDateTime = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
      
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/calendarview?` +
        `startDateTime=${startDateTime}&endDateTime=${endDateTime}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch events');
      
      const data = await response.json();
      const events = data.value.map(event => ({
        text: event.subject,
        time: new Date(event.start.dateTime).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }),
        description: event.bodyPreview || '',
        completed: false,
        date: new Date(event.start.dateTime),
        source: 'microsoft',
        externalId: event.id,
        location: event.location?.displayName,
        isExternal: true
      }));
      
      localStorage.setItem('last_calendar_sync', new Date().toISOString());
      
      if (onSyncComplete) {
        onSyncComplete(events, 'microsoft');
      }
    } catch (error) {
      console.error('Microsoft sync error:', error);
      if (error.message.includes('401')) {
        localStorage.removeItem('microsoft_calendar_token');
        setMicrosoftConnected(false);
      }
    } finally {
      setSyncing(false);
    }
  };

  const disconnectGoogle = () => {
    localStorage.removeItem('google_calendar_token');
    setGoogleConnected(false);
    if (onSyncComplete) onSyncComplete([], 'google');
  };

  const disconnectMicrosoft = () => {
    localStorage.removeItem('microsoft_calendar_token');
    setMicrosoftConnected(false);
    if (onSyncComplete) onSyncComplete([], 'microsoft');
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .sync-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          backdrop-filter: blur(8px);
        }

        .sync-modal-container {
          background: rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 7px;
          max-width: 420px;
          width: 90%;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.93);
        }

        .sync-modal-inner {
          background: white;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid rgba(213, 213, 213, 0.93);
        }

        .sync-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .sync-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .sync-close {
          border: none;
          padding: 6px;
          border-radius: 50px;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }

        .sync-close:hover {
          background: rgba(0, 0, 0, 0.08);
        }

        .calendar-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: rgba(0, 0, 0, 0.02);
          border-radius: 12px;
          margin-bottom: 12px;
          transition: all 0.2s ease;
        }

        .calendar-item:hover {
          background: rgba(0, 0, 0, 0.04);
        }

        .calendar-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .calendar-logo {
          width: 25px;
          height: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .calendar-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .calendar-name {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }

        .sync-action-btn {
          background: #272727;
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sync-action-btn:hover {
          background: #3f3f3f;
          transform: translateY(-1px);
        }

        .sync-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sync-action-btn.disconnect {
          background: rgba(0, 0, 0, 0.04);
          color: #6b7280;
        }

        .sync-action-btn.disconnect:hover {
          background: rgba(0, 0, 0, 0.08);
          color: #374151;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div className="sync-overlay" onClick={onClose}>
        <div className="sync-modal-container" onClick={(e) => e.stopPropagation()}>
                  {/* Blur overlay - same size as container, on top */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: 'blur(18px)',
          borderRadius: '20px',
          zIndex: 10
        }} />

        {/* Coming Soon message - on top of blur */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '16px',
          textAlign: 'center',
          zIndex: 11,
          maxWidth: '350px'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '12px',
            margin: 0
          }}>
            Coming Soon
          </h2>
          <p style={{
            fontSize: '13px',
            color: '#6b7280',
            marginBottom: '20px',
            margin: '12px 0 20px 0'
          }}>
            Calendar sync is coming in future update
          </p>
          <button
            onClick={onClose}
            style={{
              background: '#000000ff',
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#3f3f3f';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#272727';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Got it
          </button>
        </div>
          <div className="sync-modal-inner">
            <div className="sync-header">
              <h3 className="sync-title">Sync Calendar</h3>
              <button className="sync-close" onClick={onClose}>
                <X size={18} color="#6b7280" />
              </button>
            </div>

            {/* Google Calendar */}
            <div className="calendar-item">
              <div className="calendar-left">
                <div className="calendar-logo">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="Google Calendar" />
                </div>
                <span className="calendar-name">Google Calendar</span>
              </div>
              {googleConnected ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="sync-action-btn" 
                    onClick={syncGoogleEvents}
                    disabled={syncing}
                  >
                    <RefreshCw size={12} className={syncing ? 'spinning' : ''} />
                    Sync
                  </button>
                  <button 
                    className="sync-action-btn disconnect" 
                    onClick={disconnectGoogle}
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <button className="sync-action-btn" onClick={connectGoogleCalendar}>
                  Connect
                </button>
              )}
            </div>

            {/* Microsoft Teams Calendar */}
            <div className="calendar-item">
              <div className="calendar-left">
                <div className="calendar-logo">
<img
  src="https://icon2.cleanpng.com/20240402/aex/transparent-teams-logo-microsoft-teams-logo-with-group-of-people660ca6dfd1a983.72035149.webp"
  alt="Microsoft Teams"
/>

                </div>
                <span className="calendar-name">Teams Calendar</span>
              </div>
              {microsoftConnected ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="sync-action-btn" 
                    onClick={syncMicrosoftEvents}
                    disabled={syncing}
                  >
                    <RefreshCw size={12} className={syncing ? 'spinning' : ''} />
                    Sync
                  </button>
                  <button 
                    className="sync-action-btn disconnect" 
                    onClick={disconnectMicrosoft}
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <button className="sync-action-btn" onClick={connectMicrosoftCalendar}>
                  Connect
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const handleOAuthCallback = () => {
  const hash = window.location.hash;
  if (hash && hash.includes('access_token')) {
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const scope = params.get('scope');
    
    if (scope?.includes('calendar')) {
      if (scope.includes('googleapis')) {
        localStorage.setItem('google_calendar_token', accessToken);
      } else if (scope.includes('microsoft') || scope.includes('outlook')) {
        localStorage.setItem('microsoft_calendar_token', accessToken);
      }
      
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
    }
  }
};

export const mergeExternalEvents = (tasksByDate, externalEvents) => {
  const merged = { ...tasksByDate };
  
  externalEvents.forEach(event => {
    const dateKey = event.date.getDate();
    if (!merged[dateKey]) {
      merged[dateKey] = [];
    }
    
    const exists = merged[dateKey].some(e => 
      e.externalId === event.externalId || 
      (e.text === event.text && e.time === event.time && e.isExternal)
    );
    
    if (!exists) {
      merged[dateKey].push(event);
    }
  });
  
  return merged;
};

export default CalendarSyncModal;